import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getEmployees, manualEntry, getTimeEntries, deleteEntry, getActiveEntry, clockOut } from '../api';
import { formatDuration, formatTimer, getInitials } from '../utils';
import { useAuth } from '../AuthContext';

export default function TimeTracker() {
  const { user, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [activeEntry, setActiveEntry] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [entries, setEntries] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ clock_in: '', clock_out: '', note: '' });
  const timerRef = useRef(null);

  const loadEmployees = useCallback(async () => {
    if (!isAdmin && user?.employee_id) {
      // Employees are locked to themselves
      const emps = await getEmployees();
      const self = emps.find(e => e.id === user.employee_id);
      setEmployees(self ? [self] : []);
      setSelectedEmp(user.employee_id);
      return;
    }
    const emps = await getEmployees();
    setEmployees(emps);
    if (emps.length && !selectedEmp) setSelectedEmp(emps[0].id);
  }, [isAdmin, user?.employee_id, selectedEmp]);

  const loadEntries = useCallback(async () => {
    if (!selectedEmp) return;
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    const data = await getTimeEntries({ employee_id: selectedEmp, from });
    setEntries(data);
  }, [selectedEmp]);

  const loadActive = useCallback(async () => {
    if (!selectedEmp) return;
    const entry = await getActiveEntry(selectedEmp);
    setActiveEntry(entry);
    if (entry) {
      const secs = Math.floor((Date.now() - new Date(entry.clock_in)) / 1000);
      setElapsed(secs);
    } else {
      setElapsed(0);
    }
  }, [selectedEmp]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { if (selectedEmp) { loadActive(); loadEntries(); } }, [selectedEmp, loadActive, loadEntries]);

  useEffect(() => {
    if (activeEntry) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [activeEntry]);

  const handleManual = async (e) => {
    e.preventDefault();
    await manualEntry({ employee_id: selectedEmp, ...manual });
    setShowManual(false);
    setManual({ clock_in: '', clock_out: '', note: '' });
    await loadEntries();
  };

  // Admin-only: force-close a stuck/stale active session (e.g. the agent was
  // uninstalled without clocking out). Employees have no browser clock-out.
  const handleForceClockOut = async () => {
    if (!window.confirm(`Force clock-out ${emp?.name || 'this employee'}? This closes their currently open session.`)) return;
    try { await clockOut(selectedEmp); } catch (e) { alert('Could not clock out: ' + (e.response?.data?.error || e.message)); }
    await loadActive();
    await loadEntries();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await deleteEntry(id);
    await loadEntries();
  };

  const emp = employees.find(e => e.id === selectedEmp);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Timesheet</h1>
        {isAdmin && <button className="btn btn-outline" onClick={() => setShowManual(true)}>+ Manual Entry</button>}
      </div>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="card-title">{isAdmin ? 'Select Employee' : 'My Time'}</div>
          {isAdmin && (
            <div className="form-group">
              <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}

          {emp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div className="employee-avatar" style={{ width: 48, height: 48, fontSize: '1rem' }}>{getInitials(emp.name)}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{emp.name}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>{emp.department} · {emp.role}</div>
              </div>
            </div>
          )}

          <div style={{ padding: '12px 14px', background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--muted)' }}>
            ⏱️ Clock in and clock out happen in the <strong>HRTracking desktop app</strong> only — that's what
            captures screenshots and activity. This page is for viewing your timesheet.
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-primary btn-sm" onClick={() => window.__setPage && window.__setPage('downloads')}>
                ⬇ Get the Desktop App
              </button>
            </div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {activeEntry ? (
            <>
              <div className="badge badge-green" style={{ marginBottom: 12, fontSize: '0.875rem' }}>● Recording (desktop app)</div>
              <div className={`timer-display timer-pulse`}>{formatTimer(elapsed)}</div>
              <div className="text-muted mt-4" style={{ fontSize: '0.8rem' }}>
                Started at {new Date(activeEntry.clock_in).toLocaleTimeString()}
              </div>
            </>
          ) : (
            <>
              <div className="badge badge-gray" style={{ marginBottom: 12, fontSize: '0.875rem' }}>○ Not tracking</div>
              <div className="timer-display" style={{ color: 'var(--muted)' }}>00:00:00</div>
              <div className="text-muted mt-4" style={{ fontSize: '0.8rem' }}>Clock in from the desktop app to start tracking</div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Recent Entries</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Duration</th><th>Note</th><th>Type</th><th></th></tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan="7" className="empty">No entries yet</td></tr>
              )}
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td>{new Date(entry.clock_in).toLocaleDateString()}</td>
                  <td>{new Date(entry.clock_in).toLocaleTimeString()}</td>
                  <td>{entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString() : <span className="badge badge-green">Active</span>}</td>
                  <td>{entry.duration_seconds ? formatDuration(entry.duration_seconds) : '—'}</td>
                  <td>{entry.note || '—'}</td>
                  <td><span className={`badge ${entry.manual ? 'badge-orange' : 'badge-blue'}`}>{entry.manual ? 'Manual' : 'Auto'}</span></td>
                  <td>
                    {activeEntry && entry.id === activeEntry.id ? (
                      isAdmin ? (
                        <button className="btn btn-danger btn-sm" onClick={handleForceClockOut} title="Force-close this open session">Clock Out</button>
                      ) : null
                    ) : (
                      <button className="btn btn-outline btn-sm" onClick={() => handleDelete(entry.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showManual && (
        <div className="modal-overlay" onClick={() => setShowManual(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Manual Time Entry</div>
            <form onSubmit={handleManual}>
              <div className="form-group">
                <label>Employee</label>
                <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Clock In</label>
                  <input type="datetime-local" value={manual.clock_in} onChange={e => setManual(m => ({ ...m, clock_in: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Clock Out</label>
                  <input type="datetime-local" value={manual.clock_out} onChange={e => setManual(m => ({ ...m, clock_out: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label>Note</label>
                <input type="text" value={manual.note} onChange={e => setManual(m => ({ ...m, note: e.target.value }))} placeholder="Optional note" />
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowManual(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
